import { Css, useTestIds } from "@homebound/beam";
import { Card } from "src/components/Card";
import { Maybe } from "src/generated/graphqlTypes";
import { PlanPackageElevationsV2_ReadyPlanOptionFragment } from "src/routes/libraries/plan-package-v2/components/ElevationsTab.generated";
import { PLAN_FALLBACK_IMG } from "src/routes/libraries/plan-package/takeoffs/utils";

type ElevationCardProps = {
  elevation: PlanPackageElevationsV2_ReadyPlanOptionFragment;
  planCode: Maybe<string>;
  onClick?: VoidFunction;
};

export function ElevationCard({ elevation, planCode, onClick }: ElevationCardProps) {
  const tid = useTestIds({}, "elevationCard");
  const { code, name, optionImage, programData: pd } = elevation;
  return (
    <Card xss={Css.p0.dg.$} onClick={onClick} {...tid}>
      <div
        css={
          Css.relative.w100
            .hPx(200)
            .bgGray200.add("backgroundImage", `url(${optionImage?.previewUrl ?? PLAN_FALLBACK_IMG})`)
            .add("backgroundSize", "cover")
            .add("backgroundPosition", "center").$
        }
      />
      <div css={Css.p3.df.fdc.aifs.gap2.asStretch.$}>
        <div>
          <div css={Css.sm.gray900.$} {...tid.code}>
            {planCode}
          </div>
          <div css={Css.xl.$} {...tid.title}>
            {code} - {name}
          </div>
        </div>
        <div css={Css.dg.gtc("1fr 1fr").asStretch.rgPx(4).cg4.sm.gray900.$} {...tid.stats}>
          <span>{sellableRange(elevation)} Sq Ft</span>
          <span>Height: {pd.buildingHeightInFeet ?? "-"} ft</span>
          <span>Depth: {pd.depthInFeet ?? "-"} ft</span>
          <span>Width: {pd.widthInFeet ?? "-"} ft</span>
        </div>
      </div>
    </Card>
  );
}

/** The elevation's own sellable sq ft, widened by options that add or subtract from it. */
function sellableRange(elevation: PlanPackageElevationsV2_ReadyPlanOptionFragment) {
  const base = elevation.programData.sellableSqft;
  if (base == null) return "-";
  // A record shared with one other option holds that option's impact on this elevation
  const deltas = elevation.programDatas
    .filter((pd) => pd.readyPlanOptions.length === 2)
    .map((pd) => pd.programData.sellableSqft ?? 0);
  const min = base + deltas.filter((d) => d < 0).sum();
  const max = base + deltas.filter((d) => d > 0).sum();
  return min === max ? `${base}` : `${min} - ${max}`;
}
